import React, {
  createContext,
  useContext,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type Provider,
  type SetStateAction,
} from "react";
import type { FileExtended } from "./FileUploadInput.helpers";
import type { FileUploadedRefType } from "./types";

export type FileUploadInputContextType<
  T extends FileUploadedRefType = FileUploadedRefType,
> = {
  files: Array<FileExtended<T>>;
  setFiles: Dispatch<SetStateAction<Array<FileExtended<T>>>>;
  abortedFiles: Array<FileExtended<T>>;
  setAbortedFiles: Dispatch<SetStateAction<Array<FileExtended<T>>>>;
  isEveryFileUploaded: boolean;
  isEveryFileDownloaded: boolean;
  areFilesBeingDownloaded: boolean;
  areFilesBeingUploaded: boolean;
};

const FileUploadInputContext = createContext<FileUploadInputContextType | null>(
  null
);

export const useFileUploadInputContext = <
  T extends FileUploadedRefType = FileUploadedRefType,
>() => {
  const ctx = useContext(
    FileUploadInputContext
  ) as FileUploadInputContextType<T> | null;

  if (ctx === null) {
    throw new Error(
      "Must be called within the FileUploadInputContextProvider."
    );
  }

  return ctx;
};

export const FileUploadInputContextProvider = <T extends FileUploadedRefType>({
  children,
}: PropsWithChildren<{
  defaultValue?: T[];
}>) => {
  const [files, setFiles] = useState<Array<FileExtended<T>>>([]);
  const [abortedFiles, setAbortedFiles] = useState<Array<FileExtended<T>>>([]);

  const isEveryFileUploaded = files.length
    ? files.every((file) => !!file.fileUploaded || file.error)
    : false;
  const isEveryFileDownloaded = files.length
    ? files.every((file) => !!file.file || file.error)
    : false;
  const areFilesBeingDownloaded = Boolean(
    !isEveryFileDownloaded && files.some((f) => f.startedProcessing)
  );
  const areFilesBeingUploaded = Boolean(
    !isEveryFileUploaded && files.some((f) => f.startedProcessing)
  );

  // intentional casting
  const Provider = FileUploadInputContext.Provider as unknown as Provider<
    FileUploadInputContextType<T>
  >;

  return (
    <Provider
      value={{
        files,
        setFiles,
        abortedFiles,
        setAbortedFiles,
        isEveryFileUploaded,
        isEveryFileDownloaded,
        areFilesBeingDownloaded,
        areFilesBeingUploaded,
      }}
    >
      {children}
    </Provider>
  );
};
